/* eslint-disable max-len */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from 'app/AppStore';
import { ArrowLeftFullIcon, ArrowRightFullIcon, CameraIcon } from 'assets/icons';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { getPicture } from 'domains/pictures/Pictures.store';
import { CreatePromotionStepProps } from 'domains/promotion/Promotion.types';
import { getUserRights, UserRole } from 'domains/user';
import { AdditionalPromotionComponent } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/AdditionalPromotionComponent';
import {
  createPromotionPadding,
  SCard,
} from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/CreatePromotion.styled';
import CreatePromotionHeader from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/CreatePromotionHeader';
import { PromotionVisual } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PromotionDetailsStep/PromotionVisual';
import PromotionImageEmpty from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/PromotionImageEmpty';
import SystemImages from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/SystemImages';
import { usePromotionImages } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/hooks';
import { SModal } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/PromotionModal/PromotionModal.styled';
import { theme } from 'styles';
import {
  BannerImageSource,
  BlueButton,
  Box,
  CenteredSpin,
  CenterFlex,
  EditableBanner,
  Flex,
  Icon,
  IconButton,
  MarginBox,
  Text,
} from 'UI';
import { getSearchData } from 'utils';

const ChangeImageWrapper = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
`;
const SwitchImagesWrapper = styled.div<{ right: boolean }>`
  position: absolute;
  top: calc(50% - 20px);
  ${({ right }) => (right ? 'right: -20px' : 'left: -20px')};
`;

const SBox = styled(Box)`
  border-radius: 8px;
  :hover {
    cursor: pointer;
  }
`;

const PromotionImageSelection = ({
  promotion,
  setPromotion,
  setShowAdditionalSection,
  'data-cy': dataCy,
}: CreatePromotionStepProps & { setShowAdditionalSection: (b: boolean) => void }) => {
  const [isOpenImagesModal, setIsOpenImagesModal] = useState(false);
  const [imageSettingState, setImageSettingState] = useState<'image' | 'customImage'>('image');
  const [showImageType, setShowImageType] = useState<'primary' | 'additional'>('primary');
  const userRights = useSelector(getUserRights);
  const defaultPromoImages = getSearchData(usePromotionImages());
  const promotionImage = promotion?.image;
  const promotionAdditionalImage = promotion?.additionalInformation?.image;
  const bannerImage = useSelector((state: RootState) => getPicture(state, promotionImage?.key));
  const additionalImage = useSelector((state: RootState) => getPicture(state, promotionAdditionalImage?.key));
  const imageBase64 = bannerImage ?? `${promotionImage?.base64}`;
  const additionalImageBase64 = additionalImage ?? `${promotionAdditionalImage?.base64}`;
  const { t } = useTranslation();

  const handleCancelImageModal = () => {
    setImageSettingState('image');
    setIsOpenImagesModal(false);
  };

  const renderVisual = () => {
    return (
      <>
        {promotion.containsCustomImage && (
          <>
            <MarginBox mt={30} />
            <Text type={'h4_paragraph_title'}>
              {promotion.promotionType === 'DISCOUNT' && userRights.includes(UserRole.COUNTRY_ADMIN)
                ? t('backoffice.promotion.add_primary_visual', 'Add your primary visual')
                : t('backoffice.promotion.add_visual', 'Add your visual')}
            </Text>
            <MarginBox mt={30} />
            <AdditionalPromotionComponent
              promotionType={promotion.promotionType}
              defaultComponent={() => (
                <PromotionVisual
                  promotionId={promotion.promotionId}
                  editing={promotion.editing}
                  image={promotion.image}
                  handleSetFileBase64={(fileName: string, newImageBase64: string) =>
                    setPromotion({
                      ...promotion,
                      image: {
                        base64: newImageBase64,
                        name: fileName,
                        extension: fileName.split('.').pop() ?? '',
                      },
                    })
                  }
                  handleSetNoFile={() =>
                    setPromotion({
                      ...promotion,
                      image: undefined,
                    })
                  }
                />
              )}
              additionalTitle={t('backoffice.promotion.additional_visual', 'Additional visual')}
              additionalComponent={() => (
                <PromotionVisual
                  promotionId={promotion.promotionId}
                  editing={promotion.editing}
                  image={promotion.additionalInformation?.image}
                  handleSetFileBase64={(fileName: string, newImageBase64: string) =>
                    setPromotion({
                      ...promotion,
                      additionalInformation: {
                        ...promotion.additionalInformation,
                        image: {
                          base64: newImageBase64,
                          name: fileName,
                          extension: fileName.split('.').pop() ?? '',
                        },
                      },
                    })
                  }
                  handleSetNoFile={() =>
                    setPromotion({
                      ...promotion,
                      additionalInformation: {
                        ...promotion.additionalInformation,
                        image: undefined,
                      },
                    })
                  }
                />
              )}
              handleAddAdditionalComponent={() =>
                setPromotion({
                  ...promotion,
                  additionalInformation: { ...promotion.additionalInformation, containsAdditionalImage: true },
                })
              }
              handleRemoveAdditionalComponent={() =>
                setPromotion({
                  ...promotion,
                  additionalInformation: {
                    ...promotion.additionalInformation,
                    containsAdditionalImage: false,
                    image: undefined,
                  },
                })
              }
              showAdditionalComponent={promotion.additionalInformation.containsAdditionalImage}
            />
          </>
        )}
        {promotionImage?.base64 && (
          <CenterFlex>
            <Box>
              <BlueButton onClick={handleCancelImageModal} data-cy={'use-this-picture-button'}>
                {t('promotion.actions.use_this_picture', 'Use this picture')}
              </BlueButton>
            </Box>
          </CenterFlex>
        )}
      </>
    );
  };

  const renderImageSelection = () => {
    switch (imageSettingState) {
      case 'customImage': {
        return <>{renderVisual()}</>;
      }
      default: {
        return (
          <Flex direction={'column'} gap={25} data-cy={dataCy}>
            <Text type={'h4_paragraph_title'}>{t('backoffice.promotion.image.select_picture', 'Select picture')}</Text>
            <SystemImages
              promotion={promotion}
              setPromotion={setPromotion}
              setIsOpenImagesModal={setIsOpenImagesModal}
            />
            <CenterFlex>
              <Text
                type={'h5_light'}
                decoration={'underline'}
                displayStyle={'link'}
                cursor={'pointer'}
                onClick={() => setImageSettingState('customImage')}
                data-cy={'click-to-upload-picture-text'}
              >
                {t(
                  'backoffice.promotion.image.click_to_upload_own_image',
                  'Click to upload your own image. Supported extension: Jpg, Png. File size max 2 MB.',
                )}
              </Text>
            </CenterFlex>
          </Flex>
        );
      }
    }
  };

  const renderBanner = (image: string, dataType: BannerImageSource) => {
    return (
      <Flex direction={'column'} gap={25}>
        {promotion.containsCustomImage && (
          <Flex direction={'column'}>
            <SCard data-cy={'promotion-picture-added'}>
              <DataContainer
                data={promotionImage}
                Loading={() => (
                  <Box height={250}>
                    <CenteredSpin />
                  </Box>
                )}
                ErrorState={() => (
                  <Box height={250}>
                    <ErrorWithLabel />
                  </Box>
                )}
              >
                <div style={{ position: 'relative' }}>
                  <ChangeImageWrapper>
                    <IconButton
                      IconComponent={CameraIcon}
                      size={24}
                      onClick={() => {
                        setImageSettingState('image');
                        setIsOpenImagesModal(true);
                      }}
                      color={'clear_blue'}
                      hoverColor={'clear_blue'}
                      boxSize={'smd'}
                      bgColor={'grey85'}
                      hoverFill={'grey85'}
                      data-cy={'reselect-picture-icon'}
                    />
                  </ChangeImageWrapper>
                  {promotion.additionalInformation.image && (
                    <SwitchImagesWrapper right={showImageType === 'primary'}>
                      <SBox
                        width={40}
                        height={40}
                        background={theme.color.grey15}
                        onClick={() => {
                          setShowImageType((prevState) => {
                            if (prevState === 'primary') {
                              setShowAdditionalSection(true);
                              return 'additional';
                            }
                            setShowAdditionalSection(false);
                            return 'primary';
                          });
                        }}
                      >
                        <CenterFlex>
                          {showImageType === 'primary' ? (
                            <Icon IconComponent={ArrowRightFullIcon} size={16} color={theme.color.white} />
                          ) : (
                            <Icon IconComponent={ArrowLeftFullIcon} size={16} color={theme.color.white} />
                          )}
                        </CenterFlex>
                      </SBox>
                    </SwitchImagesWrapper>
                  )}
                  <EditableBanner
                    dark
                    image={image}
                    dataType={dataType}
                    disableTextArea={dataType === 'BASE64'}
                    buttonLabel={t('promotions.see_offers', 'See offers')}
                    title={showImageType === 'primary' ? promotion.title : promotion.additionalInformation.title}
                    handleChangeTitle={(title: string) => {
                      if (showImageType === 'primary') {
                        setPromotion({
                          ...promotion,
                          title,
                        });
                      } else {
                        setPromotion({
                          ...promotion,
                          additionalInformation: { ...promotion.additionalInformation, title },
                        });
                      }
                    }}
                    description={
                      showImageType === 'primary' ? promotion.catchLine : promotion.additionalInformation.catchLine
                    }
                    handleChangeDescription={(description: string) => {
                      if (showImageType === 'primary') {
                        setPromotion({
                          ...promotion,
                          catchLine: description,
                        });
                      } else {
                        setPromotion({
                          ...promotion,
                          additionalInformation: { ...promotion.additionalInformation, catchLine: description },
                        });
                      }
                    }}
                  />
                </div>
              </DataContainer>
            </SCard>
            <MarginBox mt={30} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderBanners = () => {
    if (!promotionImage) {
      return <PromotionImageEmpty onClick={() => setIsOpenImagesModal(true)} />;
    }

    const defaultPromoUrl = defaultPromoImages?.images.find((i) => i.imageKey === promotionImage.key)?.imageUrl;
    const defaultPromoUrlAdditional = defaultPromoImages?.images.find(
      (i) => i.imageKey === promotionAdditionalImage?.key,
    )?.imageUrl;

    return (
      <Flex direction={'column'}>
        {showImageType === 'primary' &&
          renderBanner(
            (promotionImage.base64 ? imageBase64 : defaultPromoUrl) ?? '',
            promotionImage.base64 ? 'BASE64' : 'URL',
          )}
        {showImageType === 'additional' && promotionAdditionalImage && (
          <>
            <Text type="heading3">{t('backoffice.promotion.image.additional_image', 'Additional image')}</Text>
            {renderBanner(
              (promotionAdditionalImage.base64 ? additionalImageBase64 : defaultPromoUrlAdditional) ?? '',
              promotionAdditionalImage.base64 ? 'BASE64' : 'URL',
            )}
          </>
        )}
      </Flex>
    );
  };

  return (
    <>
      <SModal
        open={isOpenImagesModal}
        onCancel={handleCancelImageModal}
        zIndex={950}
        footer={''}
        mask={false}
        marginX={0}
      >
        <Flex direction={'column'} minHeight={650} maxHeight={650}>
          <CreatePromotionHeader
            currentStep={0}
            prevStep={handleCancelImageModal}
            handleCancel={handleCancelImageModal}
          />
          <Flex direction={'column'} padding={createPromotionPadding} overflowY={'scroll'}>
            {renderImageSelection()}
          </Flex>
        </Flex>
      </SModal>
      {renderBanners()}
    </>
  );
};

export default PromotionImageSelection;
